// Petits composants et crochets partagés par toutes les pages.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Api } from "../data/api.ts";
import type { Organization, Permission, Role } from "../data/types.ts";

// ---------------------------------------------------------------------------
// Contexte de l'application
// ---------------------------------------------------------------------------
export interface AppCtx {
  api: Api;
  org: Organization;
  refreshOrg: () => Promise<void>;
  leaveDemo: () => void;
  role: Role;
  /** L'utilisateur peut-il modifier cette partie ? */
  can: (p: Permission) => boolean;
}

export function permissionsOf(org: Organization, role: Role): (p: Permission) => boolean {
  if (role === "admin") return () => true;
  const list = org.permissions?.[role] || [];
  return (p) => list.includes(p);
}
export const AppContext = createContext<AppCtx | null>(null);
export function useApp(): AppCtx {
  const c = useContext(AppContext);
  if (!c) throw new Error("AppContext manquant");
  return c;
}

// ---------------------------------------------------------------------------
// Navigation (hash)
// ---------------------------------------------------------------------------
export function currentPath(): string[] {
  return (location.hash.replace(/^#\/?/, "").split("?")[0] || "").split("/").filter(Boolean).map(decodeURIComponent);
}
export function useRoute(): string[] {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const f = () => setPath(currentPath());
    window.addEventListener("hashchange", f);
    return () => window.removeEventListener("hashchange", f);
  }, []);
  return path;
}
export function navigate(to: string): void {
  const h = "#/" + to.replace(/^[#/]+/, "");
  if (location.hash !== h) location.hash = h;
}

// ---------------------------------------------------------------------------
// Données : lecture asynchrone, rechargée à chaque modification (et en option à intervalle)
// ---------------------------------------------------------------------------
export function useData<T>(api: Api, fn: () => Promise<T>, deps: unknown[], poll = 0) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const alive = useRef(true);
  const reload = useCallback(async () => {
    try {
      const v = await fnRef.current();
      if (alive.current) { setData(v); setError(null); }
    } catch (e) {
      if (alive.current) setError((e as Error).message || "Erreur");
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    setLoading(true);
    reload();
    const unsub = api.subscribe(reload);
    const timer = poll ? window.setInterval(() => { if (!document.hidden) reload(); }, poll) : 0;
    return () => { alive.current = false; unsub(); if (timer) clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ...deps]);
  return { data, error, loading, reload };
}

export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}

// ---------------------------------------------------------------------------
// Messages éphémères
// ---------------------------------------------------------------------------
type ToastKind = "ok" | "err" | "info";
const toastListeners = new Set<(t: { id: number; text: string; kind: ToastKind }) => void>();
let toastSeq = 0;
export function toast(text: string, kind: ToastKind = "ok"): void {
  const t = { id: ++toastSeq, text, kind };
  toastListeners.forEach((f) => f(t));
}
export function Toasts() {
  const [items, setItems] = useState<{ id: number; text: string; kind: ToastKind }[]>([]);
  useEffect(() => {
    const f = (t: { id: number; text: string; kind: ToastKind }) => {
      setItems((x) => [...x, t]);
      setTimeout(() => setItems((x) => x.filter((y) => y.id !== t.id)), t.kind === "err" ? 6000 : 3200);
    };
    toastListeners.add(f);
    return () => { toastListeners.delete(f); };
  }, []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`}>
          <i aria-hidden="true">{t.kind === "err" ? "!" : t.kind === "info" ? "i" : <Check size={12} />}</i>{t.text}
        </div>
      ))}
    </div>
  );
}

/** Exécute une action et affiche l'erreur éventuelle. */
export async function attempt<T>(fn: () => Promise<T>, okText?: string): Promise<T | undefined> {
  try {
    const r = await fn();
    if (okText) toast(okText);
    return r;
  } catch (e) {
    toast((e as Error).message || "Une erreur est survenue", "err");
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Composants
// ---------------------------------------------------------------------------
export function Logo({ size = 26, light = false }: { size?: number; light?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path d="M4 10 L18 15 L18 42 L4 37 Z" fill={light ? "#F2F0EB" : "#1C1F23"} />
      <path d="M18 15 L32 10 L32 37 L18 42 Z" fill="#C2410C" />
      <path d="M32 10 L45 15 L45 42 L32 37 Z" fill="#A8A49B" />
    </svg>
  );
}

export function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  return (
    <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? "modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, hint, children, wide = false }: { label: string; hint?: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`field ${wide ? "field-wide" : ""}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{children && <div>{children}</div>}</div>;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="skeleton" aria-busy="true" aria-label="Chargement">
      <span className="sk sk-title" />
      {Array.from({ length: rows }, (_, i) => <span key={i} className="sk" style={{ width: `${92 - i * 9}%` }} />)}
    </div>
  );
}

/** Nombre qui monte jusqu'à sa valeur quand elle change (chiffres clés). */
export function useCountUp(value: number, ms = 600): number {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const reduce = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || from.current === value) { from.current = value; setShown(value); return; }
    const start = performance.now(), a = from.current;
    let raf = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      const e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(a + (value - a) * e));
      if (k < 1) raf = requestAnimationFrame(step); else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

/** Coche dessinée (la police n'a pas de glyphe ✓). */
export function Check({ size = 20 }: { size?: number }) {
  return (
    <svg className="check-svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4.5 12.5l5 5L19.5 7" />
    </svg>
  );
}

export function Icon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return <div className="error-box">{message}</div>;
}

export function Badge({ tone = "", children }: { tone?: string; children: ReactNode }) {
  return <span className={`badge ${tone ? "badge-" + tone : ""}`}>{children}</span>;
}

/** Couleur stable par format de sortie, comme sur la maquette. */
const FORMAT_COLORS: Record<string, string> = {
  A4: "#1F5FAD", A5: "#0F766E", DL: "#8A4B0F", A6: "#6D4CA8", A3: "#B4535B", "21x21": "#2E7D5B", SRA3: "#5C6B7A", "Carré 15": "#9C6B12",
};
export function formatColor(f: string): string {
  if (FORMAT_COLORS[f]) return FORMAT_COLORS[f];
  let h = 0;
  for (const c of f) h = (h * 31 + c.charCodeAt(0)) % 360;
  return `hsl(${h} 45% 38%)`;
}
